/* eslint-disable */
import React from 'react';
import PropTypes from 'prop-types';

// material-ui
import {
  Button,
  CardContent,
  CardActions,
  Divider,
  Grid,
  IconButton,
  Modal,
  TextField,
  FormHelperText,
  Typography,
  Box,
  Autocomplete,
  FormLabel,
  Chip,
  Checkbox,
  ListItemText,
  Stack
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

// project imports
import MainCard from 'ui-component/cards/MainCard';

// assets
import CloseIcon from '@mui/icons-material/Close';

// Random modal positioning
function rand() {
  return Math.round(Math.random() * 20) - 10;
}

function getModalStyle() {
  const top = 50;
  const left = 50;
  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`
  };
}

// Modal body containing the Complex form (create/edit)
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import axiosServices from 'utils/axios';
import { IconUserCircle } from '@tabler/icons';
import { AccountCircle } from '@mui/icons-material';

const ComplexModalBody = React.forwardRef(({ modalStyle, handleClose, initialValues, onSubmit, editComplex, userList }, ref) => {
  // Define validation for name and address
  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Complex name is required'),
  });
  
  // Set the title based on whether we're editing (has an id) or creating a new complex
  const title = editComplex ? "Edit Complex" : "Create Complex";

  return (
    <div ref={ref} tabIndex={-1}>
      <MainCard
        style={modalStyle}
        sx={{
          position: 'absolute',
          width: { xs: 350, lg: '70%' },
        }}
        title={title}
        content={false}
        secondary={
          <IconButton onClick={handleClose} size="large">
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ errors, handleChange, handleBlur, handleSubmit, values, touched, isSubmitting, setFieldValue }) => {
            // Determine the selected users by filtering userList with values.userIds
            const selectedUsers = userList.filter((u) => values.userIds.includes(u.id));
            return (
              <form noValidate onSubmit={handleSubmit}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6} lg={4}>
                      <TextField
                        fullWidth
                        required
                        label="Complex Name"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.name && errors.name && (
                        <FormHelperText error>{errors.name}</FormHelperText>
                      )}
                    </Grid>
                    {/* userList */}
                    <Grid item xs={12} md={6} lg={8}>
                        <Autocomplete
                          multiple
                          id="userIds"
                          options={userList}
                          getOptionLabel={(option) =>
                            `${option.firstName || ''} ${option.lastName || ''} (${option.roleName})`
                          }
                          value={
                            values.userIds && values.userIds.length
                              ? userList.filter((u) => values.userIds.includes(u.id))
                              : []
                          }
                          onChange={(event, newValue) => {
                            // Update the Formik field "userIds" with the array of selected user IDs
                            setFieldValue('userIds', newValue.map((u) => u.id));
                          }}
                          onBlur={handleBlur}
                          renderTags={() => null}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select Users"
                              variant="outlined"
                              name="userIds"
                              error={touched.userIds && Boolean(errors.userIds)}
                              helperText={touched.userIds && errors.userIds}
                            />
                          )}
                          renderOption={(props, option, { selected }) => (
                            <li {...props}>
                              <Checkbox checked={selected} sx={{ mr: 1 }} />
                              <ListItemText primary={`${option.firstName} ${option.lastName} (${option.roleName})`} />
                            </li>
                          )}
                        />
                      </Grid>
                      {/* Display Selected Users Horizontally */}
                      <Grid item xs={12}>
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              Users with Access
                            </Typography>
                            {selectedUsers && selectedUsers.length > 0 ? (
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {selectedUsers.map((u) => (
                                  <Box
                                    key={u.id}
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      border: '1px solid #ccc',
                                      borderRadius: 1,
                                      p: 1,
                                    }}
                                  >
                                    <Stack direction='row' width={1} justifyContent='flex-end'>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const newUserIds = values.userIds.filter((id) => id !== u.id);
                                          setFieldValue('userIds', newUserIds);
                                        }}
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                    <AccountCircle sx={{ fontSize: 48 }} />
                                    <Box sx={{ ml: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                      <Typography variant="body2">
                                        {u.firstName} {u.lastName}
                                      </Typography>
                                      <Typography variant="caption">{u.roleName}</Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No Users Added Yet
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                  </Grid>
                </CardContent>
                <Divider />
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <Button variant="outlined" color="secondary" onClick={handleClose}>
                    Cancel
                  </Button>
                  <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                    {title}
                  </LoadingButton>
                </CardActions>
              </form>
            )}}
        </Formik>
      </MainCard>
    </div>
  );
});

ComplexModalBody.propTypes = {
  modalStyle: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  userList: PropTypes.array.isRequired
};

export default function CreateComplexModal({ open, setOpen, editComplex, setEditComplex, fetchComplexes }) {
  const [modalStyle] = React.useState(getModalStyle);
  const [userList, setUserList] = React.useState([]);

  // Fetch list of all users to display in the dropdown.
  React.useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Adjust the endpoint as necessary. Here, we assume '/user/all' returns { users: [...] }.
        const { data } = await axiosServices.get('/user?page=1&limit=1000', {
          headers: { authorization: `Bearer ${localStorage.getItem('serviceToken')}` }
        });
        const filteredUsers = (data.users || []).filter(
          (user) => user.roleName !== 'ADMIN' && user.roleName !== 'VISITOR'
        );
        setUserList(filteredUsers);
      } catch (error) {
        toast.error('Error fetching users');
      }
    };
    fetchUsers();
  }, []);

  // Set initial values, including the join field "userIds" derived from editComplex if available.
  const initialValues = {
    name: editComplex ? editComplex.name : '',
    userIds:
      editComplex && editComplex.complexUsers
        ? editComplex.complexUsers.map((cu) => cu.userId)
        : []
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (editComplex) {
        // Update existing complex
        await axiosServices.patch(`/complex/${editComplex.id}`, values, {
          headers: { authorization: `Bearer ${localStorage.getItem('serviceToken')}` }
        });
        toast.success('Complex updated successfully');
      } else {
        // Create new complex
        await axiosServices.post(`/complex`, values, {
          headers: { authorization: `Bearer ${localStorage.getItem('serviceToken')}` }
        });
        toast.success('Complex created successfully');
      }
      resetForm();
      setEditComplex(null);
      fetchComplexes();
      handleClose();
    } catch (error) {
      console.error(error?.error?.message);
      toast.error(error?.error?.message);
    }
    setSubmitting(false);
  };

  const handleClose = () => {
    setEditComplex(null);
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="complex-modal-title"
      aria-describedby="complex-modal-description"
    >
      <ComplexModalBody
        modalStyle={modalStyle}
        handleClose={handleClose}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        editComplex={editComplex}
        userList={userList}
      />
    </Modal>
  );
}

CreateComplexModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired
};

CreateComplexModal.defaultProps = {
  initialValues: { name: '', address: '' }
};
